/**
 * @module stores/preferencesStore
 * @description User preferences store for item types and fields configuration
 *
 * Manages which item types are enabled and which fields are visible per type.
 * Preferences are persisted to IndexedDB.
 */

import { writable, derived, get } from 'svelte/store';
import { indexedDBService } from '@services/indexedDBService';
import { ITEM_TYPES, getFieldsForItemType } from '@/data';
import type { UserPreferences } from '@types';
import { DEFAULT_USER_PREFERENCES } from '@types';

// =============================================================================
// STORE
// =============================================================================

/** Internal store state */
const store = writable<UserPreferences>(DEFAULT_USER_PREFERENCES);

// =============================================================================
// DERIVED STORES
// =============================================================================

/** List of enabled item types */
export const enabledItemTypes = derived(store, ($s) => $s.enabledItemTypes);

/** Fields configuration per item type */
export const fieldsByItemType = derived(store, ($s) => $s.fieldsByItemType);

/** Get enabled item types as objects with labels */
export const enabledItemTypesWithConfig = derived(store, ($s) => {
  return $s.enabledItemTypes
    .filter((type) => ITEM_TYPES[type])
    .map((type) => ({
      value: type,
      ...ITEM_TYPES[type],
    }));
});

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Initialize preferences from storage
 */
export function initPreferences(): void {
  const stored = indexedDBService.getUserPreferences();
  store.set(stored);
}

/**
 * Check if an item type is enabled
 */
export function isItemTypeEnabled(itemType: string): boolean {
  const state = get(store);
  return state.enabledItemTypes.includes(itemType);
}

/**
 * Enable or disable an item type
 */
export function setItemTypeEnabled(itemType: string, enabled: boolean): void {
  store.update((s) => {
    const types = new Set(s.enabledItemTypes);
    if (enabled) {
      types.add(itemType);
    } else {
      types.delete(itemType);
    }
    const updated = { ...s, enabledItemTypes: Array.from(types) };
    indexedDBService.setUserPreferences(updated);
    return updated;
  });
}

/**
 * Set all enabled item types at once
 */
export function setEnabledItemTypes(itemTypes: string[]): void {
  store.update((s) => {
    const updated = { ...s, enabledItemTypes: itemTypes };
    indexedDBService.setUserPreferences(updated);
    return updated;
  });
}

/**
 * Get visible fields for an item type
 * Returns configured fields, or all fields if not configured
 */
export function getVisibleFields(itemType: string): string[] {
  const state = get(store);
  const configured = state.fieldsByItemType[itemType];
  if (configured && configured.length > 0) {
    return configured;
  }
  // Return all fields if not configured
  return getFieldsForItemType(itemType);
}

/**
 * Set visible fields for an item type
 */
export function setVisibleFields(itemType: string, fields: string[]): void {
  store.update((s) => {
    const updated = {
      ...s,
      fieldsByItemType: {
        ...s.fieldsByItemType,
        [itemType]: fields,
      },
    };
    indexedDBService.setUserPreferences(updated);
    return updated;
  });
}

/**
 * Reset fields for an item type to show all
 */
export function resetFieldsForType(itemType: string): void {
  store.update((s) => {
    const { [itemType]: _, ...rest } = s.fieldsByItemType;
    const updated = {
      ...s,
      fieldsByItemType: rest,
    };
    indexedDBService.setUserPreferences(updated);
    return updated;
  });
}

/**
 * Reset all preferences to defaults
 */
export function resetPreferences(): void {
  store.set({ ...DEFAULT_USER_PREFERENCES });
  indexedDBService.setUserPreferences(DEFAULT_USER_PREFERENCES);
}

// =============================================================================
// EXPORTS
// =============================================================================

export { store as preferencesStore };
